import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { showUnfinished } from '../site-config';

// Plate knowledge base (owner 2026-10-30): what PlateX knows about a plate,
// served by the assistant Lambda's public /plate-knowledge. One request per
// registration, shared by the plate detail hero (description) and Similar
// (readings). dev2 / local only, like the assistant widget.
export interface PlateKnowledgeReading {
  reads_as: string;
  kind: string;
  confirmed: boolean;
  car?: string;
}

// Approved plate description (reading, traits, desirability; never prices or
// sale history). null when there is no approved public description or it is
// hidden by reports.
export interface PlateDescription {
  text: string;
  note_id: string;
  version: number;
  status?: 'approved';
  beta?: boolean;
}

export interface PlateKnowledge {
  registration: string;
  readings: PlateKnowledgeReading[];
  description?: PlateDescription | null;
}

export type DescriptionReportReason =
  | 'incorrect'
  | 'reading'
  | 'offensive'
  | 'out_of_date'
  | 'spelling'
  | 'other';

// POST /plate-knowledge/report body. `website` is a honeypot: always empty
// from a real visitor (the server drops reports that fill it).
export interface DescriptionReport {
  reg: string;
  note_id: string;
  version: number;
  reason: DescriptionReportReason;
  comment?: string;
  email?: string;
  website: string;
}

// ok: accepted (hidden: the description is now hidden pending review).
// error: message to show (rate limit, mismatch, not saved).
export interface DescriptionReportResult {
  ok: boolean;
  hidden?: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class PlateKnowledgeService {
  private cache = new Map<string, Promise<PlateKnowledge | null>>();

  public get enabled(): boolean {
    const env: any = environment;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    // Plate descriptions are withheld on staging and production.
    const hostOk = showUnfinished &&
      (host.includes('dev2') || host === 'localhost' || host === '127.0.0.1');
    return !!env.assistantUrl && hostOk && typeof fetch !== 'undefined';
  }

  private get base(): string {
    return ((environment as any).assistantUrl || '').replace(/\/$/, '');
  }

  // Resolves null when disabled, on failure or after 4s. Cached per session.
  public get(reg: string): Promise<PlateKnowledge | null> {
    const key = (reg || '').replace(/\s+/g, '').toUpperCase();
    if (!key || !this.enabled) return Promise.resolve(null);
    let p = this.cache.get(key);
    if (!p) {
      const url = `${this.base}/plate-knowledge?reg=${encodeURIComponent(key)}`;
      const timer = new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000));
      p = Promise.race([
        fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        timer,
      ]) as Promise<PlateKnowledge | null>;
      this.cache.set(key, p);
    }
    return p;
  }

  // "Seen an error?" report on a description.
  public report(body: DescriptionReport): Promise<DescriptionReportResult> {
    const failed: DescriptionReportResult = {
      ok: false,
      error: 'Sorry, that did not send. Please try again.',
    };
    if (!this.enabled) return Promise.resolve(failed);
    return fetch(`${this.base}/plate-knowledge/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) =>
        r
          .json()
          .catch(() => ({}))
          .then((j: any) =>
            r.ok && j && j.ok
              ? { ok: true, hidden: !!j.hidden }
              : r.status === 429 && j && j.error
              ? { ok: false, error: j.error }
              : failed
          )
      )
      .catch(() => failed);
  }
}
